import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { FE_ROLES } from '../../config/consts';
import featureFlags from '../../config/featureFlags';
import { getCompanyDataSelector } from '../../containers/BillingInfo/selectors';
import { UserRoles } from '../../modules/user/store';
import { useAppContext } from '../../pages/App/AppContextProvider';
import { useHasFeature } from '../Feature/Feature';
import { IconBox } from '../IconBox';
import UserIcon from '../UserIcon';
import messages from './messages';
import UserRoleInfo from './UserRoleInfo';

export interface IUserRoleSelectProps {
  companyName: string;
  defaultUserRole?: string;
  onChange: (newRole: string) => void;
  userRole: string;
}

export function UserRoleSelect({ companyName, defaultUserRole, onChange, userRole }: IUserRoleSelectProps) {
  const { feSiteRoles } = useAppContext();
  const isHegMaintenance = useHasFeature(featureFlags.HEG_MAINTENANCE);
  const companyData = useSelector(getCompanyDataSelector);
  const { formatMessage } = useIntl();

  const isEditedCompanyAdmin = userRole === FE_ROLES.COMPANY_ADMIN;

  return (
    <>
      <TextField
        data-testid="user-role-select"
        fullWidth
        label={formatMessage(messages.userRoleSelectLabel)}
        onChange={(event) => onChange(event.target.value)}
        select
        slotProps={{ select: { renderValue: (selected) => formatMessage(userRoleMap[selected as string]) } }}
        value={userRole}
        variant="outlined"
      >
        <MenuItem disabled={isEditedCompanyAdmin} value={FE_ROLES.SITE_USER}>
          <IconBox icon={<UserIcon color="secondary" userRole={FE_ROLES.SITE_USER as UserRoles} />}>
            <FormattedMessage {...messages.userRoleSelectItemSiteUser} />
          </IconBox>
        </MenuItem>
        <MenuItem disabled={isEditedCompanyAdmin} value={FE_ROLES.LOGIN_LESS}>
          <IconBox icon={<UserIcon color="secondary" userRole={FE_ROLES.LOGIN_LESS as UserRoles} />}>
            <FormattedMessage {...messages.userRoleSelectItemSiteUserWithoutLogin} />
          </IconBox>
        </MenuItem>

        <MenuItem disabled={isEditedCompanyAdmin} value={FE_ROLES.SITE_ADMIN}>
          <IconBox icon={<UserIcon color="secondary" userRole={FE_ROLES.SITE_ADMIN as UserRoles} />}>
            <FormattedMessage {...messages.userRoleSelectItemSiteAdmin} />
          </IconBox>
        </MenuItem>
        <MenuItem
          disabled={
            isEditedCompanyAdmin ||
            (!feSiteRoles.isCompanyAdmin && !isEditedCompanyAdmin) ||
            (isHegMaintenance && companyData.isFilled)
          }
          value={FE_ROLES.SITE_ADMIN_PAYMENT}
        >
          <IconBox icon={<UserIcon color="secondary" userRole={FE_ROLES.SITE_ADMIN_PAYMENT as UserRoles} />}>
            <FormattedMessage {...messages.userRoleSelectItemSiteAdminPayment} />
          </IconBox>
        </MenuItem>
        <MenuItem disabled={!isEditedCompanyAdmin} value={FE_ROLES.COMPANY_ADMIN}>
          <IconBox icon={<UserIcon color="secondary" userRole={FE_ROLES.COMPANY_ADMIN as UserRoles} />}>
            <FormattedMessage {...messages.userRoleSelectItemCompanyAdmin} />
          </IconBox>
        </MenuItem>
      </TextField>
      <FormHelperText sx={{ ml: 2 }}>
        <FormattedMessage {...messages.userRoleSelectHelperText} />
      </FormHelperText>
      {userRole !== defaultUserRole && <UserRoleInfo companyName={companyName} userRole={userRole} />}
    </>
  );
}

const userRoleMap: Record<string, { id: string; defaultMessage: string }> = {
  [FE_ROLES.SITE_USER]: messages.userRoleInfoSITE_USERSelected,
  [FE_ROLES.COMPANY_ADMIN]: messages.userRoleInfoCOMPANY_ADMINSelected,
  [FE_ROLES.SITE_ADMIN]: messages.userRoleInfoSITE_ADMINSelected,
  [FE_ROLES.SITE_ADMIN_PAYMENT]: messages.userRoleInfoSITE_ADMIN_PAYMENTSelected,
  [FE_ROLES.LOGIN_LESS]: messages.userRoleInfoLOGIN_LESSSelected,
  [FE_ROLES.PMS_LOGIN_LESS]: messages.userRoleInfoPMS_LOGIN_LESSSelected,
};
